import React, { useState, forwardRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  TextField,
  Box,
  FormControlLabel,
  Radio,
  Divider,
  IconButton,
} from '@mui/material';
import Iconify from '../common/iconify.component';
import {
  formatTimestamp,
  getCurrentTimestamp,
} from '../../utils/datetime.util';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const CancelSubscription = ({
  title,
  message,
  onCancel,
  onSuccess,
  successButtonText,
  cancelButtonText,
  subscriptionData,
}) => {
  const [processing, setProcessing] = useState(false);
  const [cancelType, setCancelType] = useState('immediately');
  const [refundType, setRefundType] = useState('noRefund');
  const [refundAmount, setRefundAmount] = useState(null);
  const [refundError, setRefundError] = useState('');

  const currentTime = getCurrentTimestamp();

  const refundCalculation = useMemo(() => {
    const subscriptionDuration =
      subscriptionData.endDate - subscriptionData.startDate;
    const remainingDuration = subscriptionData.endDate - currentTime;

    const unusedAmount = (
      (remainingDuration / subscriptionDuration) *
      subscriptionData.totalAmount
    ).toFixed(2);

    return unusedAmount;
  }, [subscriptionData, currentTime]);

  const handleRefundAmountChange = (e) => {
    const { value } = e.target;
    setRefundAmount(value);

    if (value < 0) {
      setRefundError('Enter a valid amount');
    } else if (value > subscriptionData.totalAmount) {
      setRefundError(
        `Amount cannot exceed to total amount $${subscriptionData.totalAmount}`
      );
    } else {
      setRefundError('');
    }
  };

  const handleApiCall = () => {
    if (
      refundType === 'lastPayment' &&
      cancelType === 'immediately' &&
      (refundAmount < 0 || refundAmount > subscriptionData.totalAmount)
    ) {
      return;
    }

    setProcessing(true);
    onSuccess({
      immediateCancel: cancelType === 'immediately',
      refund: refundType !== 'noRefund',
      refundAmount,
    });
  };

  useEffect(() => {
    if (refundType === 'lastPayment' && cancelType === 'immediately') {
      setRefundAmount(refundCalculation);
    }
  }, [refundType, cancelType, refundCalculation]);

  return (
    <Dialog
      open
      onClose={() => {
        if (!processing) {
          onCancel();
        }
      }}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      fullWidth
      TransitionComponent={Transition}
    >
      <DialogTitle
        id="confirm-dialog-title"
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        <Iconify icon="ic:warning" width={24} sx={{ color: 'red', mr: 1 }} />
        {title}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        size="small"
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
        disabled={processing}
      >
        <Iconify icon="ic:outline-close" />
      </IconButton>

      <DialogContent id="confirm-dialog-description" dividers>
        {message}

        <Box mt={2}>
          <Box display="flex" gap={5}>
            <Box mt={1} fontWeight="bold">
              Cancel
            </Box>
            <Box display="flex" flexDirection="column" gap={2}>
              <FormControlLabel
                value="immediately"
                control={
                  <Radio
                    checked={cancelType === 'immediately'}
                    onChange={(e) => setCancelType(e.target.value)}
                  />
                }
                label={
                  <>
                    <Box fontWeight="bold">Immediately</Box>
                    <Typography variant="caption">
                      {formatTimestamp(getCurrentTimestamp(), 'DD-MMM-YYYY')}
                    </Typography>
                  </>
                }
                disabled={processing}
              />

              <FormControlLabel
                value="endOfCurrentPeriod"
                control={
                  <Radio
                    checked={cancelType === 'endOfCurrentPeriod'}
                    onChange={(e) => {
                      setCancelType(e.target.value);
                      setRefundType('noRefund');
                    }}
                  />
                }
                label={
                  <>
                    <Box fontWeight="bold">End of the current period</Box>
                    <Typography variant="caption">
                      {formatTimestamp(subscriptionData.endDate, 'DD-MMM-YYYY')}
                    </Typography>
                  </>
                }
                disabled={processing}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          <Box display="flex" gap={5}>
            <Box mt={1} fontWeight="bold">
              Refund
            </Box>
            <Box display="flex" flexDirection="column">
              <FormControlLabel
                value="noRefund"
                control={
                  <Radio
                    checked={refundType === 'noRefund'}
                    onChange={(e) => setRefundType(e.target.value)}
                  />
                }
                label={
                  <>
                    <Box fontWeight="bold">No refund</Box>
                    {cancelType === 'endOfCurrentPeriod' && (
                      <Typography variant="caption">
                        You can&apos;t issue a refund here when cancelling at
                        the end of the period.
                      </Typography>
                    )}
                  </>
                }
                disabled={processing || cancelType === 'endOfCurrentPeriod'}
              />

              {cancelType === 'immediately' && (
                <FormControlLabel
                  value="lastPayment"
                  control={
                    <Radio
                      checked={refundType === 'lastPayment'}
                      onChange={(e) => setRefundType(e.target.value)}
                    />
                  }
                  label={
                    <Box fontWeight="bold">{`Last payment $${subscriptionData.totalAmount}`}</Box>
                  }
                  disabled={processing}
                />
              )}

              {refundType === 'lastPayment' && (
                <TextField
                  id="refund_amount"
                  name="refund_amount"
                  type="number"
                  label="Amount"
                  variant="filled"
                  value={refundAmount}
                  onChange={(e) => handleRefundAmountChange(e)}
                  error={!!refundError}
                  helperText={refundError}
                  sx={{ width: '100%', my: 1 }}
                  disabled={processing}
                />
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          color="error"
          variant="contained"
          onClick={() => handleApiCall()}
          disabled={processing}
        >
          {successButtonText}
        </Button>
        <Button
          color="secondary"
          variant="contained"
          onClick={onCancel}
          disabled={processing}
        >
          {cancelButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CancelSubscription.propTypes = {
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  successButtonText: PropTypes.string,
  cancelButtonText: PropTypes.string,
  subscriptionData: PropTypes.object.isRequired,
};
CancelSubscription.defaultProps = {
  successButtonText: 'Yes',
  cancelButtonText: 'No',
};

export default CancelSubscription;
